// src/components/CredentialExposureView.js
// Credential Exposure — GLOBAL, SUPER ADMIN ONLY. Search-first console (Option C):
// search hero + stat cards + results table, with CSV upload / paste import.
//
// Route: /cti/credential-exposure  (under the CTI dropdown, beneath Cybersecurity News)
// Prop:  isSuperAdmin

import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '@clerk/clerk-react';
import {
  Search, Database, Globe, User, Hash, Bug, MapPin, KeyRound,
  Upload, X, Copy, Check, AlertTriangle, RefreshCw, Lock, ClipboardPaste
} from 'lucide-react';

// apiFetch takes getToken (the Clerk function), not a pre-fetched string.
// On 401 + TOKEN_EXPIRED it mints a fresh token via getToken({ skipCache: true }) and retries once.
const apiFetch = async (path, getToken, options = {}) => {
  const doFetch = async (tok) => fetch(`/api/credential-exposures${path}`, {
    ...options,
    headers: { 'Authorization': `Bearer ${tok}`, 'Accept': 'application/json', ...options.headers }
  });

  let tok = await getToken();
  let res = await doFetch(tok);

  if (res.status === 401) {
    const body = await res.clone().json().catch(() => ({}));
    if (body.code === 'TOKEN_EXPIRED') {
      console.log('[CredentialExposure] 401 received, refreshing token:', path);
      tok = await getToken({ skipCache: true });
      res = await doFetch(tok);
    }
  }
  return res;
};

const SEARCH_TYPES = [
  { value: 'all', label: 'All', icon: Database },
  { value: 'domain', label: 'Domain / URL', icon: Globe },
  { value: 'username', label: 'Username', icon: User },
  { value: 'ip', label: 'IP', icon: Hash },
  { value: 'malware', label: 'Malware', icon: Bug },
  { value: 'country', label: 'Country', icon: MapPin }
];

const EXPECTED_HEADER =
  'url,username,password_hash,hash_type,os,ip_address,country,malware_family,source,date_compromised';

const fmtDate = (x) => (x ? new Date(x).toLocaleDateString() : '—');

// ----- masked NTLM hash cell with copy button (module-level so it doesn't remount) -----
const HashCell = ({ hash }) => {
  const [copied, setCopied] = useState(false);
  if (!hash) return <span className="text-slate-600">—</span>;
  const masked = hash.length > 14 ? `${hash.slice(0, 8)}…${hash.slice(-4)}` : hash;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(hash);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch { /* clipboard blocked */ }
  };
  return (
    <span className="inline-flex items-center gap-2 font-mono text-xs text-slate-300">
      {masked}
      <button onClick={copy} className="text-slate-500 hover:text-cyan-400 transition-colors" title="Copy full hash">
        {copied ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5" />}
      </button>
    </span>
  );
};

// ----- import modal (portal to body, escapes nav/dropdown stacking contexts) -----
const ImportModal = ({ getToken, onClose, onDone }) => {
  const [tab, setTab] = useState('upload'); // 'upload' | 'paste'
  const [file, setFile] = useState(null);
  const [pasted, setPasted] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [result, setResult] = useState(null);

  const submit = async () => {
    setErr('');
    setResult(null);
    setBusy(true);
    try {
      let res;
      if (tab === 'upload') {
        if (!file) { setErr('Choose a .csv or .tsv file first.'); setBusy(false); return; }
        const fd = new FormData();
        fd.append('file', file);
        res = await apiFetch('/import', getToken, { method: 'POST', body: fd });
      } else {
        if (!pasted.trim()) { setErr('Paste some CSV data first.'); setBusy(false); return; }
        // Default the pasted block to CSV. A header row with tabs is treated as TSV.
        const isTsv = pasted.split('\n')[0].includes('\t');
        res = await apiFetch('/import', getToken, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(isTsv ? { tsv: pasted } : { csv: pasted })
        });
      }
      const body = await res.json().catch(() => ({}));
      if (!res.ok) { setErr(body.error || `Import failed (${res.status})`); setBusy(false); return; }
      setResult(body);
      onDone?.();
    } catch (e) {
      setErr(e.message || 'Import failed');
    } finally {
      setBusy(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="w-full max-w-2xl bg-slate-900 border border-slate-700/60 rounded-xl shadow-2xl shadow-black/50 ring-1 ring-black/20">
        {/* header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800/60">
          <h3 className="text-lg font-semibold text-slate-100 flex items-center gap-2">
            <Upload className="w-5 h-5 text-cyan-400" />
            Import Credential Exposures
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-200">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* tabs */}
        <div className="flex gap-2 px-6 pt-4">
          {[['upload', 'Upload file', Upload], ['paste', 'Paste data', ClipboardPaste]].map(([val, label, Icon]) => (
            <button
              key={val}
              onClick={() => { setTab(val); setErr(''); setResult(null); }}
              className={`px-3 py-2 rounded-lg text-sm transition-all flex items-center gap-2 ${
                tab === val ? 'bg-cyan-500/20 text-cyan-400' : 'text-slate-400 hover:bg-slate-800/50'
              }`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
        </div>

        {/* body */}
        <div className="px-6 py-4 space-y-3">
          {tab === 'upload' ? (
            <div>
              <label className="block text-sm text-slate-300 mb-2">Choose a .csv or .tsv file</label>
              <input
                type="file"
                accept=".csv,.tsv"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="block w-full text-sm text-slate-400 file:mr-3 file:py-2 file:px-4 file:rounded-lg
                           file:border-0 file:bg-cyan-500/20 file:text-cyan-400 hover:file:bg-cyan-500/30"
              />
            </div>
          ) : (
            <div>
              <label className="block text-sm text-slate-300 mb-2">Paste CSV / TSV rows (with header row)</label>
              <textarea
                value={pasted}
                onChange={(e) => setPasted(e.target.value)}
                rows={8}
                placeholder={EXPECTED_HEADER + '\nhttps://example.com/login,jdoe,31d6cfe0d16ae931...,NTLM,Windows 10,203.0.113.5,US,RedLine,demo,2025-12-01'}
                className="w-full bg-slate-950/60 border border-slate-700/60 rounded-lg px-3 py-2
                           text-xs font-mono text-slate-200 placeholder:text-slate-600 focus:outline-none focus:border-cyan-500/50"
              />
            </div>
          )}

          <p className="text-xs text-slate-500">
            Required columns: <span className="font-mono text-slate-400">url, username, password_hash</span>.
            Optional: <span className="font-mono text-slate-400">hash_type, os, ip_address, country, malware_family, source, date_compromised</span>.
          </p>

          {err && (
            <div className="flex items-start gap-2 text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{err}</span>
            </div>
          )}
          {result && (
            <div className="flex items-center gap-2 text-sm text-green-400 bg-green-500/10 border border-green-500/30 rounded-lg px-3 py-2">
              <Check className="w-4 h-4" />
              <span>Inserted {result.inserted}, skipped {result.skipped}.</span>
            </div>
          )}
        </div>

        {/* footer */}
        <div className="flex justify-end gap-2 px-6 py-4 border-t border-slate-800/60">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-slate-300 hover:bg-slate-800/50">
            Close
          </button>
          <button
            onClick={submit}
            disabled={busy}
            className="px-4 py-2 rounded-lg text-sm font-medium bg-cyan-500/20 text-cyan-400
                       hover:bg-cyan-500/30 disabled:opacity-50 flex items-center gap-2"
          >
            {busy ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            Import
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

const StatCard = ({ label, value }) => (
  <div className="bg-slate-800/30 border border-slate-700/50 rounded-lg px-4 py-3">
    <div className="text-2xl font-bold text-slate-100">{value ?? '—'}</div>
    <div className="text-xs text-slate-400 mt-1">{label}</div>
  </div>
);

const CredentialExposureView = ({ isSuperAdmin }) => {
  const { getToken, isSignedIn } = useAuth();

  const [query, setQuery] = useState('');
  const [type, setType] = useState('all');
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showImport, setShowImport] = useState(false);

  const loadStats = useCallback(async () => {
    try {
      const res = await apiFetch('/stats', getToken);
      if (res.ok) setStats(await res.json());
    } catch { /* non-fatal */ }
  }, [getToken]);

  const runSearch = useCallback(async (q = query, t = type) => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set('q', q.trim());
      params.set('type', t);
      params.set('limit', '200');
      const res = await apiFetch(`?${params.toString()}`, getToken);
      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        setError(b.error || `Request failed (${res.status})`);
        setRows([]);
      } else {
        setRows(await res.json());
      }
    } catch (e) {
      setError(e.message || 'Request failed');
    } finally {
      setLoading(false);
    }
  }, [getToken, query, type]);

  // Initial load: stats + most recent records
  useEffect(() => {
    if (!isSignedIn || !isSuperAdmin) return;
    loadStats();
    runSearch('', 'all');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSignedIn, isSuperAdmin]);

  const refreshAll = () => { loadStats(); runSearch(); };

  if (!isSuperAdmin) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-center px-8">
        <Lock className="w-10 h-10 text-slate-600 mb-3" />
        <h2 className="text-lg font-semibold text-slate-300">Credential Exposure</h2>
        <p className="text-sm text-slate-500 mt-1">This module is restricted to Super Admins.</p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* HEADER */}
      <div className="px-8 py-6 border-b border-slate-800/50 bg-gradient-to-r from-slate-900/50 to-slate-900/30">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-slate-100 flex items-center gap-3 mb-2">
              <KeyRound className="w-7 h-7 text-cyan-400" />
              Credential Exposure
            </h2>
            <p className="text-sm text-slate-400">
              Search sample stealer-log records — exposed credentials, host fingerprints, and malware attribution.
            </p>
          </div>
          <button
            onClick={() => setShowImport(true)}
            className="shrink-0 px-4 py-2 rounded-lg text-sm font-medium bg-cyan-500/20 text-cyan-400
                       hover:bg-cyan-500/30 flex items-center gap-2"
          >
            <Upload className="w-4 h-4" />
            Import
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-8">
        <div className="max-w-6xl mx-auto">
          {/* SEARCH HERO */}
          <div className="mb-6">
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-3">
              {SEARCH_TYPES.map((st) => {
                const Icon = st.icon;
                return (
                  <button
                    key={st.value}
                    onClick={() => { setType(st.value); runSearch(query, st.value); }}
                    className={`p-3 rounded-lg border transition-all ${
                      type === st.value
                        ? 'bg-cyan-500/20 border-cyan-500/50 text-cyan-400'
                        : 'bg-slate-800/30 border-slate-700/50 text-slate-400 hover:bg-slate-800/50'
                    }`}
                  >
                    <Icon className="w-5 h-5 mx-auto mb-1" />
                    <span className="text-xs font-medium">{st.label}</span>
                  </button>
                );
              })}
            </div>
            <div className="flex gap-3">
              <div className="flex-1 relative">
                <Search className="w-5 h-5 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && runSearch()}
                  placeholder="Search domain, username, IP, malware family, or country…"
                  className="w-full bg-slate-800/40 border border-slate-700/50 rounded-lg pl-12 pr-4 py-3
                             text-slate-200 placeholder:text-slate-500 focus:outline-none focus:border-cyan-500/50"
                />
              </div>
              <button
                onClick={() => runSearch()}
                className="px-6 py-3 rounded-lg text-sm font-medium bg-cyan-500/20 text-cyan-400 hover:bg-cyan-500/30"
              >
                Search
              </button>
            </div>
          </div>

          {/* STAT CARDS */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <StatCard label="Total records" value={stats?.total} />
            <StatCard label="Unique domains" value={stats?.unique_domains} />
            <StatCard label="Top malware" value={stats?.top_malware} />
            <StatCard label="Countries" value={stats?.countries} />
          </div>

          {/* RESULTS */}
          {error && (
            <div className="flex items-start gap-2 text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2 mb-4">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <div className="bg-slate-800/20 border border-slate-700/50 rounded-lg overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800/60">
              <span className="text-sm text-slate-400">
                {loading ? 'Loading…' : `${rows.length} record${rows.length === 1 ? '' : 's'}`}
              </span>
              <button onClick={refreshAll} className="text-slate-500 hover:text-cyan-400" title="Refresh">
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-slate-800/60">
                    <th className="px-4 py-3 font-medium">Domain / URL</th>
                    <th className="px-4 py-3 font-medium">Username</th>
                    <th className="px-4 py-3 font-medium">NTLM Hash</th>
                    <th className="px-4 py-3 font-medium">OS</th>
                    <th className="px-4 py-3 font-medium">IP</th>
                    <th className="px-4 py-3 font-medium">Country</th>
                    <th className="px-4 py-3 font-medium">Malware</th>
                    <th className="px-4 py-3 font-medium">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {!loading && rows.length === 0 && (
                    <tr>
                      <td colSpan={8} className="px-4 py-10 text-center text-slate-500">
                        No records found. Import a sample set to get started.
                      </td>
                    </tr>
                  )}
                  {rows.map((r) => (
                    <tr key={r.id} className="border-b border-slate-800/40 hover:bg-slate-800/30">
                      <td className="px-4 py-3 text-slate-300 max-w-[260px] truncate" title={r.url || ''}>
                        {r.url || '—'}
                      </td>
                      <td className="px-4 py-3 text-slate-300">{r.username || '—'}</td>
                      <td className="px-4 py-3"><HashCell hash={r.password_hash} /></td>
                      <td className="px-4 py-3 text-slate-400">{r.os || '—'}</td>
                      <td className="px-4 py-3 font-mono text-xs text-slate-400">{r.ip_address || '—'}</td>
                      <td className="px-4 py-3 text-slate-400">{r.country || '—'}</td>
                      <td className="px-4 py-3">
                        {r.malware_family ? (
                          <span className="px-2 py-1 rounded text-xs bg-red-500/15 text-red-400 border border-red-500/30">
                            {r.malware_family}
                          </span>
                        ) : <span className="text-slate-600">—</span>}
                      </td>
                      <td className="px-4 py-3 text-slate-400 whitespace-nowrap">
                        {fmtDate(r.date_compromised || r.created_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {showImport && (
        <ImportModal
          getToken={getToken}
          onClose={() => setShowImport(false)}
          onDone={refreshAll}
        />
      )}
    </div>
  );
};

export default CredentialExposureView;
